import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import moment from "moment";
import * as XLSX from "xlsx";
import { SaveAlt } from "@material-ui/icons";
import ModernDatePicker from "../../components/ModernDatePicker";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    backgroundColor: theme.palette.background.default,
  },
  filterContainer: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
  },

  dashboardContainer: {
    marginBottom: theme.spacing(2),
  },
  dashboardPaper: {
    padding: theme.spacing(2),
    height: "100%",
    backgroundColor: theme.palette.background.paper,
  }

}));

const getDurationSeconds = (start, end) => {
  if (!start) return 0;
  const endDate = end ? new Date(end) : new Date();
  return Math.floor((endDate - new Date(start)) / 1000);
};

const formatSeconds = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

const PausesReport = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [dateStart, setDateStart] = useState(moment().format("YYYY-MM-DD"));
  const [dateEnd, setDateEnd] = useState(moment().format("YYYY-MM-DD"));

  // Dash state
  const [summaryByUser, setSummaryByUser] = useState({});
  const [summaryByPause, setSummaryByPause] = useState({});

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await api.get("/user-pauses/report", {
        params: { dateStart, dateEnd }
      });
      const reports = response.data;
      setData(reports);

      // Calc dashboards
      const byUser = {};
      const byPause = {};

      reports.forEach(row => {
        const secs = getDurationSeconds(row.startAt, row.endAt);
        
        const userName = row.user?.name || "Desconhecido";
        if (!byUser[userName]) byUser[userName] = 0;
        byUser[userName] += secs;

        const pauseName = row.pause?.name || "Desconhecida";
        if (!byPause[pauseName]) byPause[pauseName] = 0;
        byPause[pauseName] += secs;
      });

      setSummaryByUser(byUser);
      setSummaryByPause(byPause);

    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const handleExportExcel = () => {
    const exportData = data.map(row => ({
      Operador: row.user?.name || "Desconhecido",
      Pausa: row.pause?.name || "Desconhecida",
      "Início": moment(row.startAt).format("DD/MM/YYYY HH:mm:ss"),
      "Fim": row.endAt ? moment(row.endAt).format("DD/MM/YYYY HH:mm:ss") : "Em andamento",
      "Tempo Gasto (HH:MM:SS)": formatSeconds(getDurationSeconds(row.startAt, row.endAt)),
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "RelatorioDePausas");
    XLSX.writeFile(wb, "relatorio-de-pausas.xlsx");
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Relatório de Pausas</Title>
      </MainHeader>
      
      <Paper className={classes.filterContainer} variant="outlined">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3} md={3}>
            <ModernDatePicker
              label="Data Inicial"
              value={dateStart ? moment(dateStart).toDate() : null}
              onChange={(newValue) => setDateStart(moment(newValue).format("YYYY-MM-DD"))}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <ModernDatePicker
              label="Data Final"
              value={dateEnd ? moment(dateEnd).toDate() : null}
              onChange={(newValue) => setDateEnd(moment(newValue).format("YYYY-MM-DD"))}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={fetchReport}
              disabled={loading}
              fullWidth
            >
              Filtrar
            </Button>
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleExportExcel}
              disabled={data.length === 0}
              startIcon={<SaveAlt />}
              fullWidth
            >
              Exportar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2} className={classes.dashboardContainer}>
        <Grid item xs={12} md={6}>
          <Paper className={classes.dashboardPaper} variant="outlined">
            <Typography variant="h6" gutterBottom>Tempo Total por Usuário</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Usuário</TableCell>
                  <TableCell align="right">Tempo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(summaryByUser).length === 0 && (
                  <TableRow><TableCell colSpan={2}>Sem dados</TableCell></TableRow>
                )}
                {Object.entries(summaryByUser)
                  .sort((a, b) => b[1] - a[1])
                  .map(([user, secs]) => (
                  <TableRow key={user}>
                    <TableCell>{user}</TableCell>
                    <TableCell align="right"><strong>{formatSeconds(secs)}</strong></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper className={classes.dashboardPaper} variant="outlined">
            <Typography variant="h6" gutterBottom>Tempo Total por Tipo de Pausa</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Motivo</TableCell>
                  <TableCell align="right">Tempo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(summaryByPause).length === 0 && (
                  <TableRow><TableCell colSpan={2}>Sem dados</TableCell></TableRow>
                )}
                {Object.entries(summaryByPause)
                  .sort((a, b) => b[1] - a[1])
                  .map(([pause, secs]) => (
                  <TableRow key={pause}>
                    <TableCell>{pause}</TableCell>
                    <TableCell align="right"><strong>{formatSeconds(secs)}</strong></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>

      <Paper className={classes.mainPaper} variant="outlined">
        <Typography variant="h6" gutterBottom>Histórico Detalhado</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Operador</TableCell>
              <TableCell>Pausa</TableCell>
              <TableCell>Início</TableCell>
              <TableCell>Fim</TableCell>
              <TableCell>Tempo Gasto</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={5} align="center">Nenhuma pausa encontrada no período.</TableCell>
              </TableRow>
            )}
            {data.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.user?.name}</TableCell>
                <TableCell>{row.pause?.name}</TableCell>
                <TableCell>{moment(row.startAt).format("DD/MM/YYYY HH:mm:ss")}</TableCell>
                <TableCell>{row.endAt ? moment(row.endAt).format("DD/MM/YYYY HH:mm:ss") : "Em andamento"}</TableCell>
                <TableCell>{formatSeconds(getDurationSeconds(row.startAt, row.endAt))}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default PausesReport;
